<template>
  <div class="container">
    <header class="text-center wizard-header-text">
      <h1>Strategy Wizard</h1>
      <p class="gb-text-3">Find specific strategies for a particular subject and area of need.</p>
    </header>

    <main>
      <WizardOverlayContainer
        :activated="wizardFocus"
        @unfocus="unfocus"
        theme="green">
        <span
          @click="unfocus"
          v-if="wizardFocus"
          @keypress.enter="unfocus"
          tabindex="0"
          class="close-button"
          role="button"
          aria-label="close strategy wizard pop-up"
        >
          <i class="fa fa-close fa-2x"/>
        </span>
        <p class="prompt">I need strategies for
          <A11yDropdown
            :dropdown-props="{
              ref:'subjects'
            }"
            :button-props="{
              id:'subjectsButton',
              class:'btn btn-lg btn-default dropdown-toggle' + ( subject ? '' : ' text-muted'),
              'aria-haspopup':'listbox',
              'aria-label':'I need strategies for',
              role:'combobox',
              'aria-controls':'subjectsDropdown',
              tabindex:0
            }"
            :dom-attributes="{
              'aria-labelledby':'subjectsButton',
              id:'subjectsDropdown',
              role:'listbox'
            }"
            @openedDropdown="wizardFocus = true"
          >
            <template slot="button-contents">{{ getStrategySubjectLabelFromValue(subject) || '(subject)' }} <span class="caret"/></template>
            <template slot="dropdown-items">
              <li v-if="loadingOptions" class="dropdown-menu-loading"><i class="fa fa-circle-o-notch fa-spin"/></li>
              <li
                class="dropdown-item"
                v-for="subjectOption in subjectOptions"
                :key="subjectOption.value"><a role="button" @click="updateRouteQuery({ field: 'subject', value: subjectOption.value })">{{ subjectOption.label }}</a></li>
            </template>
          </A11yDropdown>
        </p>


        <transition name="fade">
          <div v-if="subject">
            <p class="prompt" v-if="barrierOptions && barrierOptions.length > 0">to address a barrier related to
              <A11yDropdown
                :dropdown-props="{
                  ref:'barriers'
                }"
                :button-props="{
                  class:'btn btn-lg btn-default dropdown-toggle'+(barrier ? '' : ' text-muted'),
                  id:'barriersButton',
                  'aria-haspopup':'listbox',
                  role:'combobox',
                  'aria-controls':'barriersDropdown',
                  'aria-label':'to address a barrier related to',
                  tabindex:0
                }"
                :dom-attributes="{
                  'aria-labelledby':'barriersButton',
                  id:'barriersDropdown',
                  role:'listbox'
                }"
                @openedDropdown="wizardFocus = true"
              >
                <template slot="button-contents">{{ barrier || (subjectIsOther && '(select barrier)' || emptyTextOptions.barrier(getStrategySubjectLabelFromValue(subject))) }} <span class="caret"/></template>
                <template slot="dropdown-items">
                  <template v-if="!subjectIsOther">
                    <li class="dropdown-item"><a role="button" @click="updateRouteQuery({ field: 'barrier', value: undefined})">{{ emptyTextOptions.barrier(getStrategySubjectLabelFromValue(subject)) }}</a></li>
                    <li class="divider"/>
                  </template>
                  <li
                    class="dropdown-item"
                    v-for="barrier in barrierOptions"
                    :key="barrier"><a role="button" @click="updateRouteQuery({ field: 'barrier', value: barrier })">{{ barrier }}</a></li>
                </template>
              </A11yDropdown>
            </p>

            <p class="prompt" v-if="shouldShowParam('grades')">for students in grades
              <A11yDropdown
                :dropdown-props="{
                  ref:'grades'
                }"
                :button-props="{
                  class:'btn btn-lg btn-default dropdown-toggle' + (grades ? '' : ' text-muted'),
                  id:'gradesButton',
                  'aria-haspopup':'listbox',
                  'aria-label':'for students in grades',
                  role:'combobox',
                  'aria-controls':'gradesDropdown',
                  tabindex:0
                }"
                :dom-attributes="{
                  'aria-labelledby':'gradesButton',
                  id:'gradesDropdown',
                  role:'listbox'
                }"
                @openedDropdown="wizardFocus = true"
              >
                <template slot="button-contents">{{ gradeLabel || emptyTextOptions.grades }} <span class="caret"/></template>
                <template slot="dropdown-items">
                  <li class="dropdown-item"><a role="button" @click="updateRouteQuery({ field: 'grades', value: undefined})">{{ emptyTextOptions.grades }}</a></li>
                  <li class="divider"/>
                  <li
                    class="dropdown-item"
                    v-for="gradeObj in gradeOptions"
                    :key="gradeObj.label"><a role="button" @click="updateRouteQuery({ field: 'grades', value: gradeObj.value })">{{ gradeObj.label }}</a></li>
                </template>
              </A11yDropdown>
            </p>
            <collapse v-model="showMoreFilters">
              <p
                class="prompt"
                v-if="shouldShowParam('teachingType')"
                id="teachingTypesButton">to
                <A11yDropdown
                  ref="teachingTypesA11yDropdown"
                  :dropdown-props="{ref:'teachingTypes'}"
                  :button-props="{
                    ref:'teachingTypesButton',
                    class:'btn btn-lg btn-default dropdown-toggle' + (teachingType ? '' : ' text-muted'),
                    'aria-haspopup':'listbox',
                    role:'combobox',
                    'aria-controls':'teachingTypesDropdown',
                    'aria-labelledby':'teachingTypesButton',
                    tabindex:0
                  }"
                  :dom-attributes="{
                    'aria-labelledby':'teachingTypesButton',
                    id:'teachingTypesDropdown',
                    role:'listbox'
                  }"
                  @openedDropdown="wizardFocus = true"
                >
                  <template slot="button-contents">{{ teachingType || emptyTextOptions.teachingType }} <span class="caret"/></template>
                  <template slot="dropdown-items">
                    <li class="dropdown-item"><a role="button" @click="updateRouteQuery({ field: 'teachingType', value: undefined})">{{ emptyTextOptions.teachingType }}</a></li>
                    <li class="divider"/>
                    <li
                      class="dropdown-item"
                      v-for="teachingType in teachingTypeOptions"
                      :key="teachingType"><a role="button" @click="updateRouteQuery({ field: 'teachingType', value: teachingType })">{{ teachingType }}</a></li>
                  </template>
                </A11yDropdown>
                a skill/concept
              </p>

              <p
                class="prompt"
                v-if="shouldShowParam('setting')"
                id="instructionalSettingsButton">in a
                <A11yDropdown
                  :dropdown-props="{ref:'instructionalSettings'}"
                  :button-props="{
                    class:'btn btn-lg btn-default dropdown-toggle' + (setting ? '' : ' text-muted'),
                    'aria-haspopup':'listbox',
                    role:'combobox',
                    'aria-controls':'instructionalSettingsDropdown',
                    'aria-labelledby':'instructionalSettingsButton',
                    tabindex:0
                  }"
                  :dom-attributes="{
                    'aria-labelledby':'instructionalSettingsButton',
                    id:'instructionalSettingsDropdown',
                    role:'listbox'
                  }"
                  @openedDropdown="wizardFocus = true"
                >
                  <template slot="button-contents">{{ setting || emptyTextOptions.setting }} <span class="caret"/></template>
                  <template slot="dropdown-items">
                    <li class="dropdown-item"><a role="button" @click="updateRouteQuery({ field: 'setting', value: undefined})">{{ emptyTextOptions.setting }}</a></li>
                    <li class="divider"/>
                    <li
                      class="dropdown-item"
                      v-for="setting in settingOptions"
                      :key="setting"><a role="button" @click="updateRouteQuery({ field: 'setting', value: setting })">{{ setting }}</a></li>
                  </template>
                </A11yDropdown>
                instructional setting.
              </p>
            </collapse>
            <p>
              <a
                role="button"
                @click="toggleShowMore()"
                @keypress.enter="toggleShowMore()"
                tabindex="0"
                aria-live="polite">{{ showMoreFilters && 'Fewer options' || 'More options' }}</a>
            </p>

            <p class="text-center">
              <button
                class="btn btn-lg btn-primary btn-find-strategies"
                @click="findStrategiesClicked"
                :disabled="disableSubmitButton">Find Strategies</button>
              <a
                @click="reset"
                @keypress.enter="reset"
                tabindex="0"
                role="button"
                class="btn btn-lg btn-link btn-find-strategies">Start Over</a>
            </p>
          </div>
        </transition>

        <p class="text-right">
          <router-link
            :to="{name: 'StrategyIndex'}"
            v-if="!wizardFocus">View strategy index</router-link>
        </p>
      </WizardOverlayContainer>
    </main>
    <SubmitFeedback
      :subject="subject"
      type="Browse Strategies Wizard"/>
  </div>
</template>

<script>
import SubmitFeedback from '@/components/SubmitFeedback'
import {
  emptyTextOptions,
  strategyFiltersDataMixin,
  strategyFiltersPropsMixinFactory
} from '@/components/strategies/strategy_filters_util'
import WizardOverlayContainer from '@/components/WizardOverlayContainer'
import A11yDropdown from '@/components/A11yDropdown'

export default {
  name: 'StrategyWizard',
  components: {
    WizardOverlayContainer,
    SubmitFeedback,
    A11yDropdown
  },
  mixins: [
    strategyFiltersPropsMixinFactory({ routeName: 'StrategyWizard' }),
    strategyFiltersDataMixin
  ],
  data() {
    return {
      wizardFocus: false,
      showMoreFilters: false,
      emptyTextOptions
    }
  },
  methods: {
    unfocus() {
      this.wizardFocus = false
      this.showMoreFilters = false
    },
    reset() {
      this.showMoreFilters = false
      this.$router.replace({ name: 'StrategyWizard' })
    },
    findStrategiesClicked() {
      this.$router.push({ name: 'BrowseStrategiesPage', query: this.urlQuery })
      this.sendFiltersEvent('Submitted Strategy Wizard')
    },
    toggleShowMore() {
      this.showMoreFilters = !this.showMoreFilters
      if (this.showMoreFilters) {
        // set the focus to the first dropdown in the more options section
        this.$nextTick(() => this.$refs?.teachingTypesA11yDropdown?.$refs?.teachingTypesButton?.focus())
      }
    }
  },
  created() {
    if (this.subject) {
      this.wizardFocus = true
    }
    this.sendFiltersEvent('Viewed Strategy Wizard', {
      ref: this.$route.query.ref
    })
  }
}
</script>

<style scoped lang="less">
@import '~@/variables.less';
@import '~@/components/strategies/common.less';

header {
  margin-bottom: 60px;
}
.prompt {
  font-size: 32px;
  line-height: 60px;
  font-style: italic;
  label {
    font-weight: 400;
  }
}
.dropdown {
  font-style: normal;
}
.close-button {
  position: absolute;
  top: 16px;
  right: 20px;
}
.text-muted {
  color: @gray-light !important;
}
.btn-lg.btn-find-strategies {
  font-size: 24px;
  padding: 16px 24px;
  margin: 24px auto 12px auto;
}
.caret {
  margin-left: 10px;
  width: 10px;
}
</style>
